import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { ListChecklistSubmissionsResponse } from "@nova/schemas"
import { createQuery } from "@nova/queryable"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import useSWRInfinite from "swr/infinite"
import useSWRMutation from "swr/mutation"
import { downloadBlob } from "tools/browser/download"
import { usePilotTrackingQuery } from "../PilotTrackingQueries"

const getChecklistSubmissions = ({
    orgId,
    checklistId,
    limit,
    cursor,
    dateFrom,
    dateTo,
    userId,
}: {
    orgId: string
    checklistId: string
    limit: number
    cursor?: { filledAt: string; id: string }
    dateFrom?: string
    dateTo?: string
    userId?: string
}) => {
    const params = new URLSearchParams({ limit: String(limit) })
    if (cursor) {
        params.set("cursorFilledAt", cursor.filledAt)
        params.set("cursorId", cursor.id)
    }
    if (dateFrom) params.set("dateFrom", dateFrom)
    if (dateTo) params.set("dateTo", dateTo)
    if (userId) params.set("userId", userId)
    return fetchApiGateway("get /org/:orgId/fleet/checklist/:checklistId/submissions", {
        params: { orgId, checklistId },
        query: params.toString(),
    })
}

export const useChecklistSubmissions = ({
    checklistId,
    limit = 10,
    dateFrom,
    dateTo,
    userId,
}: {
    checklistId: string
    limit?: number
    dateFrom?: string
    dateTo?: string
    userId?: string
}) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, setSize, isLoading } = useSWRInfinite(
        (pageIndex, previousPageData: ListChecklistSubmissionsResponse | null) => {
            if (!orgId) return null
            if (pageIndex > 0 && !previousPageData?.nextCursor) return null
            return [
                "checklist-submissions",
                orgId,
                checklistId,
                dateFrom ?? null,
                dateTo ?? null,
                userId ?? null,
                previousPageData?.nextCursor ?? null,
            ]
        },
        ([, , , , , , cursor]) =>
            getChecklistSubmissions({
                orgId: orgId!,
                checklistId,
                limit,
                cursor: cursor ?? undefined,
                dateFrom,
                dateTo,
                userId,
            }),
        { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: false }
    )

    const submissions = data?.flatMap((page) => page.records) ?? []
    const lastPage = data?.[data.length - 1]
    const hasMore = !!lastPage?.nextCursor

    return {
        submissions,
        isLoading,
        hasMore,
        loadMore: () => setSize((s) => s + 1),
    }
}

export const useChecklistSubmitters = ({ checklistId }: { checklistId: string }) => {
    const query = useMemo(
        () =>
            createQuery({
                resource: "checklist_records",
                select: ["user_id"] as const,
                distinctOn: "user_id",
                filters: [
                    { field: "checklist_id", op: "=", value: checklistId },
                    { field: "user_id", op: "IS NOT NULL", value: null },
                ],
            }),
        [checklistId]
    )

    const { data, isLoading } = usePilotTrackingQuery(query)

    return { userIds: data?.map((row) => row.user_id) ?? [], isLoading }
}

const downloadChecklistSubmission = async (
    orgId: string,
    checklistId: string,
    submissionId: string
) => {
    const response = await fetchApiGateway(
        "get /org/:orgId/fleet/checklist/:checklistId/submissions/:submissionId/pdf/generate",
        {
            params: { orgId, checklistId, submissionId },
            query: new URLSearchParams({
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }).toString(),
            rawResponse: true,
        }
    )
    const disposition = response.headers.get("Content-Disposition")
    const filename = disposition?.match(/filename="(.+)"/)?.[1] ?? "checklist_submission.pdf"
    await downloadBlob(filename, await response.blob())
}

export const useDownloadChecklistSubmission = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { trigger, isMutating } = useSWRMutation(
        "download-checklist-submission",
        (_key, { arg }: { arg: { checklistId: string; submissionId: string } }) =>
            orgId
                ? downloadChecklistSubmission(orgId, arg.checklistId, arg.submissionId)
                : undefined
    )

    return { downloadChecklistSubmission: trigger, isDownloading: isMutating }
}
